import { eq, useLiveQuery } from '@tanstack/react-db';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

import { SwitchField, TextareaField, TextField } from '@/components/form-fields';
import { useDetailTitle } from '@/components/page-header';
import { EmptyState, ReadOnlyNotice, SectionCard } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth';
import {
  brandsCollection,
  categoriesCollection,
  productsCollection,
  productTagsCollection,
  strainsCollection,
} from '@/lib/collections';

/**
 * One taxonomy term: a category, a brand, a tag or a strain.
 *
 * Four tables, one screen, because they are the same shape to a merchant --
 * a name, an address and how many products carry it. Which table a term
 * came from is worked out from the id rather than passed in the URL: the
 * taxonomy list links here from four tabs and none of them should have to
 * spell out what the reader can see.
 */
export const Route = createFileRoute('/taxonomy/$termId')({
  /**
   * The stores this screen reads are synced on demand; without a
   * preload the first render sees empty tables and shows raw ids
   * where it means to show names.
   */
  loader: async () => {
    await Promise.all([
      categoriesCollection.preload(),
      brandsCollection.preload(),
      productTagsCollection.preload(),
      strainsCollection.preload(),
      productsCollection.preload(),
    ]).catch(() => undefined);
  },
  component: TaxonomyDetailPage,
});

function TaxonomyDetailPage() {
  const { termId } = useParams({ from: '/taxonomy/$termId' });
  const { can } = useAuth();

  const canManage = can('catalog.manage');

  const categories = useLiveQuery((query) =>
    query.from({ term: categoriesCollection }).where(({ term }) => eq(term.id, termId)),
  );
  const brands = useLiveQuery((query) =>
    query.from({ term: brandsCollection }).where(({ term }) => eq(term.id, termId)),
  );
  const tags = useLiveQuery((query) =>
    query.from({ term: productTagsCollection }).where(({ term }) => eq(term.id, termId)),
  );
  const strains = useLiveQuery((query) =>
    query.from({ term: strainsCollection }).where(({ term }) => eq(term.id, termId)),
  );

  const products = useLiveQuery((query) => query.from({ product: productsCollection }));

  const category = categories.data?.[0];
  const brand = brands.data?.[0];
  const tag = tags.data?.[0];
  const strain = strains.data?.[0];

  const term = category ?? brand ?? tag ?? strain;
  const kind = category ? 'Category' : brand ? 'Brand' : tag ? 'Tag' : strain ? 'Strain' : null;

  useDetailTitle(term?.name ?? 'Term');

  const used = (products.data ?? []).filter((product) => {
    if (category) return product.category_id === termId;
    if (brand) return product.brand_id === termId;
    if (strain) return product.strain_id === termId;

    return false;
  }).length;

  const loading = categories.isLoading || brands.isLoading || tags.isLoading || strains.isLoading;

  if (loading) return <p className="text-muted-foreground">Loading…</p>;

  if (!term || !kind) {
    return (
      <EmptyState>
        No such term.{' '}
        <Link to="/taxonomy" className="underline">
          Back to taxonomy
        </Link>
      </EmptyState>
    );
  }

  /*
   * One writer per table rather than a store picked at runtime: the four
   * stores have different row types, and a union of their `update` overloads
   * has no callable signature -- TypeScript is right that "whichever of these
   * four" is not a thing you can call.
   */
  const update = (patch: Record<string, unknown>) => {
    const assign = (draft: object) => Object.assign(draft, patch);

    if (category) categoriesCollection.update(termId, assign);
    else if (brand) brandsCollection.update(termId, assign);
    else if (tag) productTagsCollection.update(termId, assign);
    else if (strain) strainsCollection.update(termId, assign);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{term.name}</h1>
        <Badge variant="secondary">{kind}</Badge>
        {!tag && (
          <span className="text-muted-foreground text-sm">
            {used} {used === 1 ? 'product' : 'products'}
          </span>
        )}
      </div>

      {!canManage && <ReadOnlyNotice permission="catalog.manage" />}

      <fieldset disabled={!canManage} className="space-y-6">
        <SectionCard title={kind} description="Shown in the storefront's filters and menus.">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="term-name"
              label="Name"
              value={term.name}
              onChange={(value) => update({ name: value })}
            />
            <TextField
              id="term-slug"
              label="Address"
              value={term.slug}
              onChange={(value) => update({ slug: value })}
            />
          </div>

          {'description' in term && (
            <TextareaField
              id="term-description"
              label="Description"
              rows={3}
              value={(term.description as string | null) ?? ''}
              onChange={(value) => update({ description: value })}
            />
          )}

          {'active' in term && (
            <SwitchField
              id="term-active"
              label="Shown in the storefront"
              checked={Boolean(term.active)}
              onChange={(checked) => update({ active: checked })}
            />
          )}

          {strain && (
            <TextField
              id="term-lineage"
              label="Lineage"
              description="What it was crossed from."
              value={strain.lineage ?? ''}
              onChange={(value) => update({ lineage: value })}
            />
          )}
        </SectionCard>
      </fieldset>
    </div>
  );
}
